import { useEffect, useState, useCallback } from 'react';
import Link from 'next/link';
import { useParams } from 'next/navigation';
import {
  ArrowLeft, Loader2, AlertTriangle, Plus, Check, X,
  Trophy, Zap, Download, Calendar, ChevronDown, ChevronRight, Users, Pencil, Upload
} from 'lucide-react';
import { cn, localDatetimeToISO, isoToLocalDatetime } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth.store';
import { authFetch } from '@/lib/auth-client';
import { Button } from '@/components/ui/button';
import {
  ManualPredictionModal,
  type ManualPropForEdit,
} from '@/components/admin/ManualPredictionModal';
import { ManualPredictionCsvModal } from '@/components/admin/ManualPredictionCsvModal';

const STATUS_COLORS: Record<string, string> = {
  UPCOMING: 'bg-blue-500/10 text-blue-400 border-blue-500/30',
  OPEN: 'bg-green-500/10 text-green-400 border-green-500/30',
  LOCKED: 'bg-amber-500/10 text-amber-400 border-amber-500/30',
  SCORING: 'bg-orange-500/10 text-orange-400 border-orange-500/30',
  COMPLETED: 'bg-zinc-500/10 text-zinc-400 border-zinc-500/30',
};

const PROP_RESULT_COLORS: Record<string, string> = {
  PENDING: 'bg-zinc-500/10 text-zinc-400 border-zinc-500/30',
  HIT: 'bg-green-500/10 text-green-400 border-green-500/30',
  MISS: 'bg-red-500/10 text-red-400 border-red-500/30',
};

interface UndercardInfo {
  id: string;
  title: string;
  round: number;
  status: string;
  pointsPerCorrectPick: number;
  lockTime: string;
  startTime: string;
  endTime?: string | null;
}

interface GameRow {
  id: string;
  homeTeam: string;
  awayTeam: string;
  gameTime: string;
  winner: string | null;
  homeScore: number | null;
  awayScore: number | null;
  homeSeed: number | null;
  awaySeed: number | null;
  group: string | null;
}

interface PropRow {
  id: string;
  description: string;
  costCoins: number;
  bonusMultiplier: number;
  result: string;
  propType:
    | 'ODDS_BASED'
    | 'MANUAL_TEAM_THRESHOLD'
    | 'MANUAL_MATCH_THRESHOLD'
    | 'MANUAL_PLAYER_BOOLEAN'
    | 'MANUAL_GENERIC';
  pickMode: 'BUY_ONLY' | 'YES_NO';
  subjectTeam: string | null;
  subjectPlayer: string | null;
  threshold: number | null;
  statCategory: string | null;
  gameId: string | null;
  _count?: { selections: number };
}

interface GameProposal {
  homeTeam: string;
  awayTeam: string;
  homeAbbreviation: string;
  awayAbbreviation: string;
  gameTime: string;
  espnEventId: string;
  homeSeed: number | null;
  awaySeed: number | null;
  group: string | null;
}

// ---- Odds API grouped types ----

interface AltLine {
  line: number;
  overOdds: number;
  underOdds: number;
  costCoins: number;
  bonusMultiplier: number;
  description: string;
}

interface CategoryGroup {
  category: string;
  label: string;
  primaryLine: number;
  lines: AltLine[];
}

interface GameTeamProps {
  gameId: string;
  homeTeam: string;
  awayTeam: string;
  categories: CategoryGroup[];
}

interface PlayerCategoryGroup {
  category: string;
  label: string;
  primaryLine: number;
  lines: AltLine[];
}

interface PlayerGroup {
  playerName: string;
  categories: PlayerCategoryGroup[];
}

interface GamePlayerProps {
  gameId: string;
  homeTeam: string;
  awayTeam: string;
  players: PlayerGroup[];
}

// Selection key for tracking checked categories
function selKey(gameId: string, category: string, playerName?: string, label?: string) {
  const base = label ? `${gameId}|${category}|${label}` : `${gameId}|${category}`;
  return playerName ? `${base}|${playerName}` : base;
}

// Format "First Last" → "Last, First"
function lastFirst(name: string) {
  const parts = name.trim().split(/\s+/);
  if (parts.length <= 1) return name;
  const last = parts.pop()!;
  return `${last}, ${parts.join(' ')}`;
}

export default function UndercardDetailPage() {
  // Migrated from Next.js: original signature was
  //   ({ params: paramsPromise }: { params: Promise<{ id; undercardId }> })
  // The migrate-pages.mjs transform handled the simpler `params` case but
  // missed the renamed-destructure variant. React Router gives us both
  // segments synchronously via useParams.
  const { id: eventIdParam, undercardId: undercardIdParam } = useParams<{
    id: string;
    undercardId: string;
  }>();
  const [eventId, setEventId] = useState(eventIdParam ?? '');
  const [undercardId, setUndercardId] = useState(undercardIdParam ?? '');
  const { accessToken } = useAuthStore();

  const [undercard, setUndercard] = useState<UndercardInfo | null>(null);
  const [games, setGames] = useState<GameRow[]>([]);
  const [props, setProps] = useState<PropRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  // Games
  const [showAddGame, setShowAddGame] = useState(false);
  const [gameForm, setGameForm] = useState({ homeTeam: '', awayTeam: '', gameTime: '', homeSeed: '', awaySeed: '', group: '' });
  const [gameProposals, setGameProposals] = useState<GameProposal[]>([]);
  const [selectedGames, setSelectedGames] = useState<Set<number>>(new Set());
  const [fetchingGames, setFetchingGames] = useState(false);
  const [gameDateFrom, setGameDateFrom] = useState('');
  const [gameDateTo, setGameDateTo] = useState('');

  // Props
  const [showAddProp, setShowAddProp] = useState(false);
  const [propForm, setPropForm] = useState({ description: '', costCoins: 100, bonusMultiplier: 1.5 });

  // Manual predictions modal state
  const [manualModal, setManualModal] = useState<{
    open: boolean;
    mode: 'create' | 'edit';
    existing: ManualPropForEdit | null;
  }>({ open: false, mode: 'create', existing: null });
  const [csvModalOpen, setCsvModalOpen] = useState(false);

  // Odds API — grouped
  const [teamProps, setTeamProps] = useState<GameTeamProps[]>([]);
  const [playerProps, setPlayerProps] = useState<GamePlayerProps[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<Set<string>>(new Set());
  const [fetchingOdds, setFetchingOdds] = useState(false);
  const [importingOdds, setImportingOdds] = useState(false);
  const [showGameSelector, setShowGameSelector] = useState(false);
  const [selectedGameIds, setSelectedGameIds] = useState<Set<string>>(new Set());

  // Collapse state
  const [expandedGames, setExpandedGames] = useState<Set<string>>(new Set());
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [expandedPlayers, setExpandedPlayers] = useState<Set<string>>(new Set());

  // Settle state
  const [settlingGame, setSettlingGame] = useState<string | null>(null);
  const [settleWinner, setSettleWinner] = useState<'HOME' | 'AWAY'>('HOME');
  const [settleHomeScore, setSettleHomeScore] = useState('');
  const [settleAwayScore, setSettleAwayScore] = useState('');
  const [settlingProp, setSettlingProp] = useState<string | null>(null);
  const [settlingPropResult, setSettlingPropResult] = useState<'HIT' | 'MISS'>('HIT');
  const [settling, setSettling] = useState(false);
  const [autoSettling, setAutoSettling] = useState(false);

  // Inline editing of undercard times
  const [showTimeEdit, setShowTimeEdit] = useState(false);
  const [timeForm, setTimeForm] = useState({ startTime: '', lockTime: '', endTime: '' });
  const [savingTimes, setSavingTimes] = useState(false);

  useEffect(() => {
    if (eventIdParam) setEventId(eventIdParam);
    if (undercardIdParam) setUndercardId(undercardIdParam);
  }, [eventIdParam, undercardIdParam]);

  const showSuccess = useCallback((msg: string) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(''), 4000);
  }, []);

  const fetchData = useCallback(async () => {
    if (!eventId || !undercardId || !accessToken) return;
    try {
      const res = await authFetch(`/api/admin/main-events/${eventId}/undercards/${undercardId}`);
      const data = await res.json();
      if (res.ok) {
        setUndercard(data.data.undercard);
        setGames(data.data.games);
        setProps(data.data.props);
      } else {
        setError(data.error || `Failed to load undercard (${res.status})`);
      }
    } catch (err) {
      setError(`Failed to load undercard: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setLoading(false);
    }
  }, [eventId, undercardId, accessToken]);

  useEffect(() => {
    if (eventId && undercardId) fetchData();
  }, [eventId, undercardId, fetchData]);

  // Default game date range to undercard's start date
  useEffect(() => {
    if (undercard?.startTime && !gameDateFrom) {
      const startDate = new Date(undercard.startTime).toISOString().slice(0, 10);
      setGameDateFrom(startDate);
      setGameDateTo(startDate);
    }
  }, [undercard?.startTime, gameDateFrom]);

  // ========== ADD GAME ==========
  const handleAddGame = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!accessToken) return;
    setError('');

    try {
      const res = await authFetch(
        `/api/admin/main-events/${eventId}/undercards/${undercardId}/games`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            homeTeam: gameForm.homeTeam,
            awayTeam: gameForm.awayTeam,
            gameTime: localDatetimeToISO(gameForm.gameTime),
            homeSeed: gameForm.homeSeed ? Number(gameForm.homeSeed) : undefined,
            awaySeed: gameForm.awaySeed ? Number(gameForm.awaySeed) : undefined,
            group: gameForm.group || undefined,
          }),
        }
      );

      if (res.ok) {
        setShowAddGame(false);
        setGameForm({ homeTeam: '', awayTeam: '', gameTime: '', homeSeed: '', awaySeed: '', group: '' });
        showSuccess('Game added');
        fetchData();
      } else {
        const data = await res.json();
        setError(data.error || 'Failed to add game');
      }
    } catch {
      setError('Failed to add game');
    }
  };

  // ========== SETTLE GAME ==========
  const handleSettleGame = async () => {
    if (!settlingGame || !accessToken) return;
    setSettling(true);
    setError('');

    try {
      const res = await authFetch(`/api/admin/main-events/settle-game/${settlingGame}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          winner: settleWinner,
          homeScore: settleHomeScore ? Number(settleHomeScore) : undefined,
          awayScore: settleAwayScore ? Number(settleAwayScore) : undefined,
        }),
      });

      if (res.ok) {
        setSettlingGame(null);
        showSuccess('Game settled');
        fetchData();
      } else {
        const data = await res.json();
        setError(data.error || 'Failed to settle game');
      }
    } catch {
      setError('Failed to settle game');
    } finally {
      setSettling(false);
    }
  };

  // ========== ADD PROP ==========
  const handleAddProp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!accessToken) return;
    setError('');

    try {
      const res = await authFetch(`/api/admin/main-events/${eventId}/undercards/${undercardId}/props`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(propForm),
      });

      if (res.ok) {
        setShowAddProp(false);
        setPropForm({ description: '', costCoins: 100, bonusMultiplier: 1.5 });
        showSuccess('Prop added');
        fetchData();
      } else {
        const data = await res.json();
        setError(data.error || 'Failed to add prop');
      }
    } catch {
      setError('Failed to add prop');
    }
  };

  // ========== SETTLE PROP ==========
  const handleSettleProp = async () => {
    if (!settlingProp || !accessToken) return;
    setSettling(true);
    setError('');

    try {
      const res = await authFetch(
        `/api/admin/main-events/${eventId}/undercards/${undercardId}/props/${settlingProp}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ result: settlingPropResult }),
        }
      );

      if (res.ok) {
        setSettlingProp(null);
        showSuccess('Prop settled');
        fetchData();
      } else {
        const data = await res.json();
        setError(data.error || 'Failed to settle prop');
      }
    } catch {
      setError('Failed to settle prop');
    } finally {
      setSettling(false);
    }
  };

  // ========== SETTLE UNDERCARD ==========
  const handleSettleUndercard = async () => {
    if (!confirm('Settle this undercard? This will mark it COMPLETED and roll up scores.')) return;
    if (!accessToken) return;
    setSettling(true);
    setError('');

    try {
      const res = await authFetch(`/api/admin/main-events/settle-undercard/${undercardId}`, {
        method: 'POST',
      });

      if (res.ok) {
        showSuccess('Undercard settled!');
        fetchData();
      } else {
        const data = await res.json();
        setError(data.error || 'Failed to settle undercard');
      }
    } catch {
      setError('Failed to settle undercard');
    } finally {
      setSettling(false);
    }
  };

  const handleAutoSettle = async () => {
    if (!confirm('Auto-settle games and props using Odds API scores? This will settle all completed games and resolve their props.')) return;
    setAutoSettling(true);
    setError('');

    try {
      const res = await authFetch(
        `/api/admin/main-events/${eventId}/undercards/${undercardId}/auto-settle`,
        { method: 'POST' }
      );

      const data = await res.json();

      if (res.ok) {
        const r = data.data;
        const parts = [];
        if (r.gamesSettled > 0) parts.push(`${r.gamesSettled} games settled`);
        if (r.propsSettled > 0) parts.push(`${r.propsSettled} props settled`);
        if (r.gamesNotCompleted > 0) parts.push(`${r.gamesNotCompleted} games not yet completed`);
        if (r.gamesAlreadySettled > 0) parts.push(`${r.gamesAlreadySettled} already settled`);
        if (r.errors?.length > 0) parts.push(`${r.errors.length} errors`);
        showSuccess(parts.join(' · ') || 'No games to settle');
        if (r.errors?.length > 0) {
          setError(r.errors.join('; '));
        }
        fetchData();
      } else {
        setError(data.error || 'Auto-settle failed');
      }
    } catch (err) {
      setError(`Auto-settle failed: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setAutoSettling(false);
    }
  };

  // ========== UPDATE UNDERCARD STATUS ==========
  const handleStatusChange = async (newStatus: string) => {
    if (!accessToken) return;
    setError('');

    try {
      const res = await authFetch(`/api/admin/main-events/${eventId}/undercards/${undercardId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus }),
      });

      if (res.ok) {
        showSuccess(`Status changed to ${newStatus}`);
        fetchData();
      } else {
        const data = await res.json();
        setError(data.error || 'Failed to update status');
      }
    } catch {
      setError('Failed to update status');
    }
  };

  // ========== EDIT TIMES ==========
  const openTimeEdit = () => {
    if (!undercard) return;
    setTimeForm({
      startTime: undercard.startTime ? isoToLocalDatetime(undercard.startTime) : '',
      lockTime: undercard.lockTime ? isoToLocalDatetime(undercard.lockTime) : '',
      endTime: undercard.endTime ? isoToLocalDatetime(undercard.endTime) : '',
    });
    setShowTimeEdit(true);
  };

  const handleSaveTimes = async () => {
    if (!accessToken || savingTimes) return;
    setError('');
    if (!timeForm.startTime || !timeForm.lockTime) {
      setError('startTime and lockTime are required');
      return;
    }
    setSavingTimes(true);
    try {
      const body: Record<string, unknown> = {
        startTime: localDatetimeToISO(timeForm.startTime),
        lockTime: localDatetimeToISO(timeForm.lockTime),
        endTime: timeForm.endTime ? localDatetimeToISO(timeForm.endTime) : null,
      };
      const res = await authFetch(`/api/admin/main-events/${eventId}/undercards/${undercardId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        showSuccess('Times updated');
        setShowTimeEdit(false);
        fetchData();
      } else {
        const data = await res.json();
        setError(data.error || 'Failed to update times');
      }
    } catch {
      setError('Failed to update times');
    } finally {
      setSavingTimes(false);
    }
  };

  const shiftTimes = (deltaDays: number) => {
    setTimeForm((f) => {
      const shift = (s: string) => {
        if (!s) return s;
        const d = new Date(s);
        d.setDate(d.getDate() + deltaDays);
        return isoToLocalDatetime(d.toISOString());
      };
      return {
        startTime: shift(f.startTime),
        lockTime: shift(f.lockTime),
        endTime: shift(f.endTime),
      };
    });
  };

  // ========== FETCH ODDS API (grouped) ==========
  const handleFetchOdds = async (gameIds?: string[]) => {
    if (!accessToken) return;
    setFetchingOdds(true);
    setError('');
    setTeamProps([]);
    setPlayerProps([]);
    setSelectedCategories(new Set());
    setExpandedGames(new Set());
    setExpandedCategories(new Set());
    setExpandedPlayers(new Set());

    try {
      const url = gameIds && gameIds.length > 0
        ? `/api/admin/main-events/${eventId}/undercards/${undercardId}/fetch-odds?gameIds=${gameIds.join(',')}`
        : `/api/admin/main-events/${eventId}/undercards/${undercardId}/fetch-odds`;
      const res = await authFetch(url);

      if (res.ok) {
        const data = await res.json();
        const inner = data.data || data;
        const tp: GameTeamProps[] = inner.teamProps || [];
        const pp: GamePlayerProps[] = inner.playerProps || [];
        setTeamProps(tp);
        setPlayerProps(pp);
        // Expand all games by default
        const gameIds = new Set([
          ...tp.map((g) => g.gameId),
          ...pp.map((g) => `player-${g.gameId}`),
        ]);
        setExpandedGames(gameIds);

        // Show coverage summary
        const cov = inner.coverage;
        if (cov) {
          const parts: string[] = [`${cov.gamesMatched}/${cov.totalGames} games matched`];
          if (cov.missingSpreadTeams > 0) parts.push(`${cov.missingSpreadTeams} teams missing spreads`);
          if (cov.missingTotals > 0) parts.push(`${cov.missingTotals} games missing totals`);
          if (cov.missingGames > 0 || cov.missingSpreadTeams > 0 || cov.missingTotals > 0) {
            setError(`Coverage: ${parts.join(' · ')}`);
          } else {
            showSuccess(`Full coverage: ${parts[0]}, ${cov.teamsWithSpreads} team spreads, ${cov.gamesWithTotals} totals`);
          }
        } else if (tp.length === 0 && pp.length === 0) {
          showSuccess('No odds found for these games');
        }
      } else {
        const data = await res.json();
        setError(data.error || 'Failed to fetch odds');
      }
    } catch {
      setError('Failed to fetch odds');
    } finally {
      setFetchingOdds(false);
    }
  };

  // Toggle category selection
  const toggleCategory = (key: string) => {
    setSelectedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // Toggle game collapse
  const toggleGameExpand = (id: string) => {
    setExpandedGames((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Toggle category alt lines collapse
  const toggleCategoryExpand = (key: string) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // Toggle player collapse
  const togglePlayerExpand = (key: string) => {
    setExpandedPlayers((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // Count selected categories across team + player
  const selectedTeamCount = teamProps.reduce((acc, g) =>
    acc + g.categories.filter((c) => selectedCategories.has(selKey(g.gameId, c.category, undefined, c.label))).length, 0);
  const selectedPlayerCount = playerProps.reduce((acc, g) =>
    acc + g.players.reduce((pa, p) =>
      pa + p.categories.filter((c) => selectedCategories.has(selKey(g.gameId, c.category, p.playerName))).length, 0), 0);

  // Quick-select helpers: toggle all keys for a given category type
  const toggleTeamCategory = (categoryFilter: string) => {
    const keys = teamProps.flatMap((g) =>
      g.categories.filter((c) => c.category === categoryFilter).map((c) => selKey(g.gameId, c.category, undefined, c.label))
    );
    setSelectedCategories((prev) => {
      const next = new Set(prev);
      const allSelected = keys.length > 0 && keys.every((k) => next.has(k));
      keys.forEach((k) => allSelected ? next.delete(k) : next.add(k));
      return next;
    });
  };

  const isTeamCategoryAllSelected = (categoryFilter: string) => {
    const keys = teamProps.flatMap((g) =>
      g.categories.filter((c) => c.category === categoryFilter).map((c) => selKey(g.gameId, c.category, undefined, c.label))
    );
    return keys.length > 0 && keys.every((k) => selectedCategories.has(k));
  };

  const togglePlayerCategory = (categoryFilter: string) => {
    const keys = playerProps.flatMap((g) =>
      g.players.flatMap((p) =>
        p.categories.filter((c) => c.category === categoryFilter).map((c) => selKey(g.gameId, c.category, p.playerName))
      )
    );
    setSelectedCategories((prev) => {
      const next = new Set(prev);
      const allSelected = keys.length > 0 && keys.every((k) => next.has(k));
      keys.forEach((k) => allSelected ? next.delete(k) : next.add(k));
      return next;
    });
  };

  const isPlayerCategoryAllSelected = (categoryFilter: string) => {
    const keys = playerProps.flatMap((g) =>
      g.players.flatMap((p) =>
        p.categories.filter((c) => c.category === categoryFilter).map((c) => selKey(g.gameId, c.category, p.playerName))
      )
    );
    return keys.length > 0 && keys.every((k) => selectedCategories.has(k));
  };

  // Build keys for top N scorers per team, optionally filtered to specific categories
  const getTopScorerKeys = (countPerTeam: number, categoryFilter?: string[]) => {
    const keys: string[] = [];
    for (const g of playerProps) {
      // Split players by team (home vs away based on player name presence in team rosters isn't available,
      // so we sort all players by projected points and take top N per game — effectively per-team since
      // each game has two teams' players). For per-team selection, we approximate by taking 2x count from
      // the sorted list (top N from each team ≈ top 2N overall for a single game).
      // Better approach: take top countPerTeam * 2 from each game's player list.
      const sorted = [...g.players]
        .map((p) => ({ ...p, pts: p.categories.find((c) => c.category === 'PLAYER_POINTS')?.primaryLine ?? 0 }))
        .sort((a, b) => b.pts - a.pts)
        .slice(0, countPerTeam * 2);
      for (const p of sorted) {
        for (const c of p.categories) {
          if (categoryFilter && !categoryFilter.includes(c.category)) continue;
          keys.push(selKey(g.gameId, c.category, p.playerName));
        }
      }
    }
    return keys;
  };

  const toggleTopScorers = (countPerTeam: number, categoryFilter?: string[]) => {
    const keys = getTopScorerKeys(countPerTeam, categoryFilter);
    setSelectedCategories((prev) => {
      const next = new Set(prev);
      const allSelected = keys.length > 0 && keys.every((k) => next.has(k));
      keys.forEach((k) => allSelected ? next.delete(k) : next.add(k));
      return next;
    });
  };

  const isTopScorersAllSelected = (countPerTeam: number, categoryFilter?: string[]) => {
    const keys = getTopScorerKeys(countPerTeam, categoryFilter);
    return keys.length > 0 && keys.every((k) => selectedCategories.has(k));
  };

  // Import all selected categories (all alt lines per category)
  const handleImportSelected = async (type: 'team' | 'player') => {
    if (!accessToken) return;
    setImportingOdds(true);
    setError('');

    const propsToImport: Array<{ line: AltLine; gameId: string; category: string }> = [];

    if (type === 'team') {
      for (const g of teamProps) {
        for (const cat of g.categories) {
          if (!selectedCategories.has(selKey(g.gameId, cat.category, undefined, cat.label))) continue;
          for (const line of cat.lines) {
            propsToImport.push({ line, gameId: g.gameId, category: cat.category });
          }
        }
      }
    } else {
      for (const g of playerProps) {
        for (const player of g.players) {
          for (const cat of player.categories) {
            if (!selectedCategories.has(selKey(g.gameId, cat.category, player.playerName))) continue;
            for (const line of cat.lines) {
              propsToImport.push({ line, gameId: g.gameId, category: cat.category });
            }
          }
        }
      }
    }

    // Dedup: skip duplicate descriptions (e.g., overlapping quick-action presets)
    const seen = new Set<string>();
    const dedupedProps = propsToImport.filter(({ line }) => {
      const key = line.description.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });

    let imported = 0;
    for (const { line, gameId: gId, category: cat } of dedupedProps) {
      try {
        const res = await authFetch(`/api/admin/main-events/${eventId}/undercards/${undercardId}/props`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            description: line.description,
            costCoins: line.costCoins,
            bonusMultiplier: line.bonusMultiplier,
            gameId: gId,
            category: cat,
            line: line.line,
          }),
        });
        if (res.ok) imported++;
      } catch {}
    }

    const dupes = propsToImport.length - dedupedProps.length;
    showSuccess(`Imported ${imported} props${dupes > 0 ? ` (${dupes} duplicates skipped)` : ''}`);
    if (type === 'team') {
      setTeamProps([]);
    } else {
      setPlayerProps([]);
    }
    setSelectedCategories(new Set());
    setImportingOdds(false);
    fetchData();
  };

  // ========== FETCH GAMES API (ESPN) ==========
  const handleFetchGames = async () => {
    if (!accessToken) return;
    setFetchingGames(true);
    setError('');
    setGameProposals([]);
    setSelectedGames(new Set());

    try {
      const params = new URLSearchParams();
      if (gameDateFrom && gameDateTo && gameDateFrom !== gameDateTo) {
        params.set('dateFrom', gameDateFrom.replace(/-/g, ''));
        params.set('dateTo', gameDateTo.replace(/-/g, ''));
      } else {
        const date = gameDateFrom || gameDateTo;
        if (date) params.set('date', date.replace(/-/g, ''));
      }

      const res = await authFetch(
        `/api/admin/main-events/${eventId}/undercards/${undercardId}/fetch-games?${params}`
      );

      if (res.ok) {
        const data = await res.json();
        setGameProposals(data.proposals || []);
        if (data.proposals?.length === 0) {
          showSuccess(gameDateFrom ? `No games found for ${gameDateFrom}` : 'No upcoming games found');
        }
      } else {
        const data = await res.json();
        setError(data.error || 'Failed to fetch games');
      }
    } catch {
      setError('Failed to fetch games');
    } finally {
      setFetchingGames(false);
    }
  };

  const toggleGameSelection = (idx: number) => {
    setSelectedGames((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  const handleDeleteGame = async (gameId: string) => {
    if (!accessToken || !confirm('Delete this game and its props?')) return;
    try {
      const res = await authFetch(
        `/api/admin/main-events/${eventId}/undercards/${undercardId}/games?gameId=${gameId}`,
        { method: 'DELETE' }
      );
      if (res.ok) {
        showSuccess('Game deleted');
        fetchData();
      } else {
        setError('Failed to delete game');
      }
    } catch { setError('Failed to delete game'); }
  };

  const [refreshingRegions, setRefreshingRegions] = useState(false);

  const handleRefreshRegions = async () => {
    if (!accessToken) return;
    setRefreshingRegions(true);
    setError('');

    try {
      const res = await authFetch(
        `/api/admin/main-events/${eventId}/undercards/${undercardId}/games`,
        { method: 'PATCH' }
      );

      if (res.ok) {
        const data = await res.json();
        const { updated, total } = data.data || {};
        showSuccess(`Updated ${updated} of ${total} games with region/seed data`);
        fetchData();
      } else {
        const data = await res.json();
        setError(data.error || 'Failed to refresh regions');
      }
    } catch {
      setError('Failed to refresh regions');
    } finally {
      setRefreshingRegions(false);
    }
  };

  const handleDedup = async () => {
    if (!accessToken) return;
    try {
      const res = await authFetch(
        `/api/admin/main-events/${eventId}/undercards/${undercardId}/games?dedup=true`,
        { method: 'DELETE' }
      );
      if (res.ok) {
        const data = await res.json();
        showSuccess(`Removed ${data.data?.deleted || 0} duplicate games`);
        fetchData();
      } else {
        setError('Failed to dedup games');
      }
    } catch { setError('Failed to dedup games'); }
  };

  const handleImportSelectedGames = async () => {
    if (!accessToken || selectedGames.size === 0) return;
    setError('');

    const selected = Array.from(selectedGames).map((idx) => gameProposals[idx]);
    let imported = 0;

    for (const game of selected) {
      try {
        const res = await authFetch(
          `/api/admin/main-events/${eventId}/undercards/${undercardId}/games`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              homeTeam: game.homeTeam,
              awayTeam: game.awayTeam,
              gameTime: game.gameTime,
              espnEventId: game.espnEventId,
              homeSeed: game.homeSeed,
              awaySeed: game.awaySeed,
              group: game.group,
            }),
          }
        );
        if (res.ok) imported++;
      } catch {}
    }

    showSuccess(`Imported ${imported} games`);
    setGameProposals([]);
    setSelectedGames(new Set());
    fetchData();
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-6 w-6 text-red-400 animate-spin" />
      </div>
    );
  }

  if (!undercard) {
    return <p className="text-zinc-400 py-12 text-center">Undercard not found</p>;
  }

  const allGamesSettled = games.length > 0 && games.every((g) => g.winner !== null);
  const allPropsSettled = props.every((p) => p.result !== 'PENDING');
  const pendingPropsCount = props.filter((p) => p.result === 'PENDING').length;

  return (
    <div>
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <Link
          href={`/admin/main-events/${eventId}`}
          className="p-2 rounded-lg hover:bg-zinc-800 text-zinc-400 hover:text-white transition-colors"
        >
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-white">{undercard.title}</h1>
          <p className="text-sm text-zinc-400">
            Round {undercard.round} · {undercard.pointsPerCorrectPick} points/pick · Locks{' '}
            {new Date(undercard.lockTime).toLocaleString()}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={openTimeEdit}
            className="px-2 py-1 rounded text-xs font-medium bg-zinc-800 text-zinc-300 hover:bg-zinc-700 hover:text-white transition-colors"
            title="Edit start, lock, and end times"
          >
            Edit times
          </button>
          <span
            className={cn(
              'px-2 py-1 rounded text-xs font-bold border',
              STATUS_COLORS[undercard.status] || STATUS_COLORS.UPCOMING
            )}
          >
            {undercard.status}
          </span>
        </div>
      </div>

      {showTimeEdit && (
        <div className="mb-6 p-4 rounded-xl bg-zinc-900 border border-amber-500/30 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-bold text-amber-300">Edit Undercard Times</h3>
            <div className="flex items-center gap-1 text-[10px] text-zinc-500">
              <span className="mr-1">Quick shift:</span>
              {[-7, -1, +1, +7].map((d) => (
                <button
                  key={d}
                  type="button"
                  onClick={() => shiftTimes(d)}
                  className="px-2 py-0.5 rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-300"
                >
                  {d > 0 ? `+${d}d` : `${d}d`}
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-xs text-zinc-400 mb-1">Start Time *</label>
              <input
                type="datetime-local"
                value={timeForm.startTime}
                onChange={(e) => setTimeForm((f) => ({ ...f, startTime: e.target.value }))}
                className="w-full px-3 py-2 rounded-lg bg-zinc-950 border border-zinc-700 text-white text-sm focus:border-amber-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-xs text-zinc-400 mb-1">Lock Time *</label>
              <input
                type="datetime-local"
                value={timeForm.lockTime}
                onChange={(e) => setTimeForm((f) => ({ ...f, lockTime: e.target.value }))}
                className="w-full px-3 py-2 rounded-lg bg-zinc-950 border border-zinc-700 text-white text-sm focus:border-amber-500 focus:outline-none"
              />
              <p className="text-[10px] text-zinc-500 mt-1">After lock, picks &amp; manual predictions are blocked.</p>
            </div>
            <div>
              <label className="block text-xs text-zinc-400 mb-1">End Time (optional)</label>
              <input
                type="datetime-local"
                value={timeForm.endTime}
                onChange={(e) => setTimeForm((f) => ({ ...f, endTime: e.target.value }))}
                className="w-full px-3 py-2 rounded-lg bg-zinc-950 border border-zinc-700 text-white text-sm focus:border-amber-500 focus:outline-none"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setShowTimeEdit(false)}
              className="px-3 py-1.5 text-sm text-zinc-400 hover:text-zinc-200"
            >
              Cancel
            </button>
            <Button type="button" size="sm" onClick={handleSaveTimes} disabled={savingTimes}>
              {savingTimes ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Calendar className="h-3 w-3 mr-1" />}
              Save Times
            </Button>
          </div>
        </div>
      )}

      {error && (
        <div className="mb-6 p-4 rounded-xl bg-red-500/10 border border-red-500/30 flex items-center gap-3">
          <AlertTriangle className="h-5 w-5 text-red-400 flex-shrink-0" />
          <p className="text-red-400">{error}</p>
        </div>
      )}

      {successMsg && (
        <div className="mb-6 p-4 rounded-xl bg-green-500/10 border border-green-500/30 flex items-center gap-3">
          <Check className="h-5 w-5 text-green-400 flex-shrink-0" />
          <p className="text-green-400">{successMsg}</p>
        </div>
      )}

      {/* Status Controls */}
      <div className="bg-zinc-900 rounded-xl border border-zinc-800 p-4 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-sm text-zinc-400">Status:</span>
            {['UPCOMING', 'OPEN', 'LOCKED', 'SCORING'].map((s) => (
              <button
                key={s}
                onClick={() => handleStatusChange(s)}
                className={cn(
                  'px-3 py-1 rounded text-xs font-medium border transition-colors',
                  undercard.status === s
                    ? STATUS_COLORS[s]
                    : 'border-zinc-700 text-zinc-500 hover:text-zinc-300 hover:border-zinc-600'
                )}
              >
                {s}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            {undercard.status !== 'COMPLETED' && games.length > 0 && !allGamesSettled && (
              <Button
                size="sm"
                onClick={handleAutoSettle}
                disabled={autoSettling}
                className="bg-amber-600 hover:bg-amber-700 text-white"
              >
                {autoSettling ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Zap className="h-3 w-3 mr-1" />}
                Auto-Settle
              </Button>
            )}
            {undercard.status !== 'COMPLETED' && allGamesSettled && allPropsSettled && (
              <Button
                variant="destructive"
                size="sm"
                onClick={handleSettleUndercard}
                disabled={settling}
              >
                {settling ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Trophy className="h-3 w-3 mr-1" />}
                Settle Undercard
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* ========== GAMES (full width) ========== */}
      <div className="bg-zinc-900 rounded-xl border border-zinc-800 p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-white">Games ({games.length})</h2>
          <div className="flex items-center gap-2">
            {games.length > 1 && (
              <button
                onClick={handleDedup}
                className="flex items-center gap-1 text-sm text-zinc-400 hover:text-zinc-300 transition-colors"
              >
                <X className="h-3 w-3" />
                Dedup
              </button>
            )}
            {games.length > 0 && (
              <button
                onClick={handleRefreshRegions}
                disabled={refreshingRegions}
                className="flex items-center gap-1 text-sm text-blue-400 hover:text-blue-300 transition-colors disabled:opacity-50"
              >
                {refreshingRegions ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                Refresh Regions
              </button>
            )}
            <button
              onClick={handleFetchGames}
              disabled={fetchingGames}
              className="flex items-center gap-1 text-sm text-amber-400 hover:text-amber-300 transition-colors disabled:opacity-50"
            >
              {fetchingGames ? <Loader2 className="h-4 w-4 animate-spin" /> : <Calendar className="h-4 w-4" />}
              Fetch Games
            </button>
            <button
              onClick={() => setShowAddGame(!showAddGame)}
              className="flex items-center gap-1 text-sm text-red-400 hover:text-red-300 transition-colors"
            >
              <Plus className="h-4 w-4" />
              Add Game
            </button>
          </div>
        </div>

        {/* Game proposals from ESPN */}
        {gameProposals.length > 0 && (
          <div className="mb-4 p-4 bg-amber-500/5 rounded-lg border border-amber-500/30 space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-amber-400">
                <Calendar className="h-4 w-4 inline mr-1" />
                {gameProposals.length} upcoming games
              </p>
              <Button
                size="sm"
                disabled={selectedGames.size === 0}
                onClick={handleImportSelectedGames}
              >
                Import {selectedGames.size} Selected
              </Button>
            </div>
            <div className="space-y-1 max-h-72 overflow-y-auto">
              {(() => {
                // Group proposals by region if any have groups
                const hasGroups = gameProposals.some((g) => g.group);
                if (!hasGroups) {
                  return gameProposals.map((g, idx) => (
                    <label
                      key={g.espnEventId}
                      className={cn(
                        'flex items-center gap-2 p-2 rounded text-sm cursor-pointer transition-colors',
                        selectedGames.has(idx) ? 'bg-amber-500/10' : 'hover:bg-zinc-800'
                      )}
                    >
                      <input
                        type="checkbox"
                        checked={selectedGames.has(idx)}
                        onChange={() => toggleGameSelection(idx)}
                        className="rounded border-zinc-600"
                      />
                      <span className="text-white flex-1">
                        {g.awayAbbreviation} <span className="text-zinc-500">@</span> {g.homeAbbreviation}
                        <span className="text-zinc-600 text-xs ml-2">{g.awayTeam} @ {g.homeTeam}</span>
                      </span>
                      <span className="text-xs text-zinc-500">
                        {new Date(g.gameTime).toLocaleDateString()} {new Date(g.gameTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </label>
                  ));
                }

                // Build region groups preserving original indexes
                const regionMap = new Map<string, Array<{ g: GameProposal; idx: number }>>();
                gameProposals.forEach((g, idx) => {
                  const region = g.group || 'Other';
                  if (!regionMap.has(region)) regionMap.set(region, []);
                  regionMap.get(region)!.push({ g, idx });
                });

                const REGION_COLORS: Record<string, string> = {
                  East: 'text-blue-400 border-blue-500/30',
                  West: 'text-red-400 border-red-500/30',
                  South: 'text-green-400 border-green-500/30',
                  Midwest: 'text-amber-400 border-amber-500/30',
                };

                return Array.from(regionMap.entries()).map(([region, items]) => (
                  <div key={region} className="mb-2">
                    <div className={cn(
                      'text-xs font-semibold uppercase tracking-wider px-2 py-1 border-b mb-1',
                      REGION_COLORS[region] || 'text-zinc-400 border-zinc-700'
                    )}>
                      {region} Region ({items.length})
                    </div>
                    {items.map(({ g, idx }) => (
                      <label
                        key={g.espnEventId}
                        className={cn(
                          'flex items-center gap-2 p-2 rounded text-sm cursor-pointer transition-colors',
                          selectedGames.has(idx) ? 'bg-amber-500/10' : 'hover:bg-zinc-800'
                        )}
                      >
                        <input
                          type="checkbox"
                          checked={selectedGames.has(idx)}
                          onChange={() => toggleGameSelection(idx)}
                          className="rounded border-zinc-600"
                        />
                        <span className="text-white flex-1">
                          {g.awaySeed && <span className="text-zinc-500 text-xs">({g.awaySeed}) </span>}
                          {g.awayAbbreviation}
                          <span className="text-zinc-500 mx-1">@</span>
                          {g.homeAbbreviation}
                          {g.homeSeed && <span className="text-zinc-500 text-xs"> ({g.homeSeed})</span>}
                          <span className="text-zinc-600 text-xs ml-2">{g.awayTeam} @ {g.homeTeam}</span>
                        </span>
                        <span className="text-xs text-zinc-500">
                          {new Date(g.gameTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </label>
                    ))}
                  </div>
                ));
              })()}
            </div>
            <button
              onClick={() => { setGameProposals([]); setSelectedGames(new Set()); }}
              className="text-xs text-zinc-500 hover:text-zinc-300"
            >
              Dismiss
            </button>
          </div>
        )}

        {/* Date range for fetch games */}
        {gameProposals.length === 0 && (
          <div className="mb-4 flex items-center gap-2">
            <input
              type="date"
              value={gameDateFrom}
              onChange={(e) => setGameDateFrom(e.target.value)}
              className="px-3 py-1.5 rounded-lg bg-zinc-800 border border-zinc-700 text-white text-xs focus:border-red-500 focus:outline-none [color-scheme:dark]"
            />
            <span className="text-zinc-500 text-xs">to</span>
            <input
              type="date"
              value={gameDateTo}
              onChange={(e) => setGameDateTo(e.target.value)}
              className="px-3 py-1.5 rounded-lg bg-zinc-800 border border-zinc-700 text-white text-xs focus:border-red-500 focus:outline-none [color-scheme:dark]"
            />
            {gameDateFrom && (
              <button
                onClick={() => { setGameDateFrom(''); setGameDateTo(''); }}
                className="text-xs text-zinc-500 hover:text-zinc-300"
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </div>
        )}

        {showAddGame && (
          <form onSubmit={handleAddGame} className="mb-4 p-3 bg-zinc-800 rounded-lg border border-zinc-700 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-zinc-400 mb-1">Home Team *</label>
                <input
                  type="text"
                  value={gameForm.homeTeam}
                  onChange={(e) => setGameForm((p) => ({ ...p, homeTeam: e.target.value }))}
                  required
                  placeholder="Duke"
                  className="w-full px-3 py-2 rounded-lg bg-zinc-900 border border-zinc-700 text-white text-sm focus:border-red-500 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-xs text-zinc-400 mb-1">Away Team *</label>
                <input
                  type="text"
                  value={gameForm.awayTeam}
                  onChange={(e) => setGameForm((p) => ({ ...p, awayTeam: e.target.value }))}
                  required
                  placeholder="UNC"
                  className="w-full px-3 py-2 rounded-lg bg-zinc-900 border border-zinc-700 text-white text-sm focus:border-red-500 focus:outline-none"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs text-zinc-400 mb-1">Game Time *</label>
              <input
                type="datetime-local"
                value={gameForm.gameTime}
                onChange={(e) => setGameForm((p) => ({ ...p, gameTime: e.target.value }))}
                required
                className="w-full px-3 py-2 rounded-lg bg-zinc-900 border border-zinc-700 text-white text-sm focus:border-red-500 focus:outline-none [color-scheme:dark]"
              />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-xs text-zinc-400 mb-1">Home Seed</label>
                <input
                  type="number"
                  value={gameForm.homeSeed}
                  onChange={(e) => setGameForm((p) => ({ ...p, homeSeed: e.target.value }))}
                  min={1}
                  placeholder="1"
                  className="w-full px-3 py-2 rounded-lg bg-zinc-900 border border-zinc-700 text-white text-sm focus:border-red-500 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-xs text-zinc-400 mb-1">Away Seed</label>
                <input
                  type="number"
                  value={gameForm.awaySeed}
                  onChange={(e) => setGameForm((p) => ({ ...p, awaySeed: e.target.value }))}
                  min={1}
                  placeholder="16"
                  className="w-full px-3 py-2 rounded-lg bg-zinc-900 border border-zinc-700 text-white text-sm focus:border-red-500 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-xs text-zinc-400 mb-1">Group / Region</label>
                <input
                  type="text"
                  value={gameForm.group}
                  onChange={(e) => setGameForm((p) => ({ ...p, group: e.target.value }))}
                  placeholder="East"
                  className="w-full px-3 py-2 rounded-lg bg-zinc-900 border border-zinc-700 text-white text-sm focus:border-red-500 focus:outline-none"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setShowAddGame(false)} className="px-3 py-1.5 text-sm text-zinc-400">
                Cancel
              </button>
              <Button type="submit" size="sm">
                <Plus className="h-3 w-3 mr-1" /> Add
              </Button>
            </div>
          </form>
        )}

        {/* Settle game modal */}
        {settlingGame && (
          <div className="mb-4 p-4 bg-zinc-800 rounded-lg border border-amber-500/30 space-y-3">
            <p className="text-sm font-medium text-white">
              Settle: {games.find((g) => g.id === settlingGame)?.awayTeam} @{' '}
              {games.find((g) => g.id === settlingGame)?.homeTeam}
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-zinc-400 mb-1">Winner *</label>
                <select
                  value={settleWinner}
                  onChange={(e) => setSettleWinner(e.target.value as 'HOME' | 'AWAY')}
                  className="w-full px-3 py-2 rounded-lg bg-zinc-900 border border-zinc-700 text-white text-sm focus:border-red-500 focus:outline-none"
                >
                  <option value="HOME">{games.find((g) => g.id === settlingGame)?.homeTeam} (HOME)</option>
                  <option value="AWAY">{games.find((g) => g.id === settlingGame)?.awayTeam} (AWAY)</option>
                </select>
              </div>
              <div />
              <div>
                <label className="block text-xs text-zinc-400 mb-1">Home Score</label>
                <input
                  type="number"
                  value={settleHomeScore}
                  onChange={(e) => setSettleHomeScore(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg bg-zinc-900 border border-zinc-700 text-white text-sm focus:border-red-500 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-xs text-zinc-400 mb-1">Away Score</label>
                <input
                  type="number"
                  value={settleAwayScore}
                  onChange={(e) => setSettleAwayScore(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg bg-zinc-900 border border-zinc-700 text-white text-sm focus:border-red-500 focus:outline-none"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => { setSettlingGame(null); setSettleHomeScore(''); setSettleAwayScore(''); }}
                className="px-3 py-1.5 text-sm text-zinc-400"
              >
                Cancel
              </button>
              <Button size="sm" variant="destructive" onClick={handleSettleGame} disabled={settling}>
                {settling ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
                Settle Game
              </Button>
            </div>
          </div>
        )}

        {/* Games list */}
        {games.length === 0 ? (
          <p className="text-sm text-zinc-500 py-4 text-center">No games yet</p>
        ) : (
          <div className="space-y-2">
            {(() => {
              const REGION_COLORS: Record<string, string> = {
                East: 'text-blue-400 border-blue-500/30',
                West: 'text-red-400 border-red-500/30',
                South: 'text-green-400 border-green-500/30',
                Midwest: 'text-amber-400 border-amber-500/30',
              };

              const renderGame = (game: GameRow) => (
                <div
                  key={game.id}
                  className={cn(
                    'p-3 rounded-lg border text-sm',
                    game.winner ? 'bg-zinc-800/50 border-zinc-700' : 'bg-zinc-800 border-zinc-700'
                  )}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className={cn('font-medium', game.winner === 'AWAY' ? 'text-green-400' : 'text-white')}>
                          {game.awaySeed && <span className="text-zinc-500">({game.awaySeed}) </span>}
                          {game.awayTeam}
                          {game.awayScore !== null && <span className="text-zinc-400 ml-1">({game.awayScore})</span>}
                        </span>
                        <span className="text-zinc-600">@</span>
                        <span className={cn('font-medium', game.winner === 'HOME' ? 'text-green-400' : 'text-white')}>
                          {game.homeSeed && <span className="text-zinc-500">({game.homeSeed}) </span>}
                          {game.homeTeam}
                          {game.homeScore !== null && <span className="text-zinc-400 ml-1">({game.homeScore})</span>}
                        </span>
                      </div>
                      <p className="text-xs text-zinc-500 mt-1">
                        {new Date(game.gameTime).toLocaleString()}
                        {game.group && <span className="ml-2 text-zinc-600">· {game.group}</span>}
                      </p>
                    </div>
                    <div className="flex items-center gap-1.5">
                      {game.winner ? (
                        <span className="px-2 py-0.5 rounded text-xs font-bold bg-green-500/10 text-green-400 border border-green-500/30">
                          {game.winner}
                        </span>
                      ) : (
                        <button
                          onClick={() => {
                            setSettlingGame(game.id);
                            setSettleWinner('HOME');
                            setSettleHomeScore('');
                            setSettleAwayScore('');
                          }}
                          className="px-2 py-1 rounded text-xs font-medium bg-amber-500/10 text-amber-400 border border-amber-500/30 hover:bg-amber-500/20 transition-colors"
                        >
                          Settle
                        </button>
                      )}
                      <button
                        onClick={() => handleDeleteGame(game.id)}
                        className="p-1 rounded text-zinc-600 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                        title="Delete game"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              );

              const hasGroups = games.some((g) => g.group);
              if (!hasGroups) return games.map(renderGame);

              const regionMap = new Map<string, GameRow[]>();
              for (const g of games) {
                const region = g.group || 'Other';
                if (!regionMap.has(region)) regionMap.set(region, []);
                regionMap.get(region)!.push(g);
              }

              return Array.from(regionMap.entries()).map(([region, regionGames]) => (
                <div key={region}>
                  <div className={cn(
                    'text-xs font-semibold uppercase tracking-wider px-2 py-1.5 border-b mb-2',
                    REGION_COLORS[region] || 'text-zinc-400 border-zinc-700'
                  )}>
                    {region} Region ({regionGames.length})
                  </div>
                  <div className="space-y-2 mb-3">
                    {regionGames.map(renderGame)}
                  </div>
                </div>
              ));
            })()}
          </div>
        )}
      </div>

      {/* ========== TEAM PROPS (from Odds API) ========== */}
      {teamProps.length > 0 && (
        <div className="bg-zinc-900 rounded-xl border border-zinc-800 p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-white">
              <Download className="h-5 w-5 inline mr-2 text-amber-400" />
              Team Props
            </h2>
            <div className="flex items-center gap-3">
              <button
                onClick={() => { setTeamProps([]); setSelectedCategories(new Set()); }}
                className="text-xs text-zinc-500 hover:text-zinc-300"
              >
                Dismiss
              </button>
              <Button
                size="sm"
                disabled={selectedTeamCount === 0 || importingOdds}
                onClick={() => handleImportSelected('team')}
              >
                {importingOdds ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
                Import {selectedTeamCount} Selected
              </Button>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <span className="text-xs text-zinc-500 mr-1">Quick:</span>
            {(['SPREAD', 'TOTAL', 'MONEYLINE'] as const).map((cat) => {
              const active = isTeamCategoryAllSelected(cat);
              const label = cat === 'SPREAD' ? 'All Spreads' : cat === 'TOTAL' ? 'All Totals' : 'All Moneyline';
              return (
                <button
                  key={cat}
                  onClick={() => toggleTeamCategory(cat)}
                  className={cn(
                    'px-2.5 py-1 rounded text-xs font-medium border transition-colors',
                    active
                      ? 'bg-amber-500/20 border-amber-500/50 text-amber-400'
                      : 'border-zinc-600 text-zinc-400 hover:text-zinc-300 hover:border-zinc-500'
                  )}
                >
                  {active ? <Check className="h-3 w-3 inline mr-1" /> : null}
                  {label}
                </button>
              );
            })}
          </div>

          <div className="space-y-3">
            {teamProps.map((gtp) => {
              const isExpanded = expandedGames.has(gtp.gameId);
              const allTeamKeys = gtp.categories.map((c) => selKey(gtp.gameId, c.category, undefined, c.label));
              const allTeamSelected = allTeamKeys.length > 0 && allTeamKeys.every((k) => selectedCategories.has(k));
              return (
                <div key={gtp.gameId} className="border border-zinc-700 rounded-lg overflow-hidden">
                  <div className="flex items-center bg-zinc-800">
                    <button
                      onClick={() => toggleGameExpand(gtp.gameId)}
                      className="flex-1 flex items-center gap-2 p-3 hover:bg-zinc-750 transition-colors text-left"
                    >
                      {isExpanded ? <ChevronDown className="h-4 w-4 text-zinc-400" /> : <ChevronRight className="h-4 w-4 text-zinc-400" />}
                      <span className="text-sm font-medium text-white">{gtp.awayTeam} @ {gtp.homeTeam}</span>
                      <span className="text-xs text-zinc-500 ml-auto">{gtp.categories.length} categories</span>
                    </button>
                    <button
                      onClick={() => {
                        setSelectedCategories((prev) => {
                          const next = new Set(prev);
                          if (allTeamSelected) {
                            allTeamKeys.forEach((k) => next.delete(k));
                          } else {
                            allTeamKeys.forEach((k) => next.add(k));
                          }
                          return next;
                        });
                      }}
                      className={cn(
                        'px-3 py-1 mr-3 rounded text-xs font-medium border transition-colors',
                        allTeamSelected
                          ? 'bg-amber-500/20 border-amber-500/50 text-amber-400'
                          : 'border-zinc-600 text-zinc-400 hover:text-zinc-300 hover:border-zinc-500'
                      )}
                    >
                      {allTeamSelected ? 'Deselect All' : 'Quick Add All'}
                    </button>
                  </div>
                  {isExpanded && (
                    <div className="p-3 space-y-2">
                      {gtp.categories.map((cat) => {
                        const key = selKey(gtp.gameId, cat.category, undefined, cat.label);
                        const isSelected = selectedCategories.has(key);
                        const isCatExpanded = expandedCategories.has(key);
                        const primaryIdx = cat.lines.findIndex((l) => l.line === cat.primaryLine);
                        const primaryLineData = cat.lines[primaryIdx >= 0 ? primaryIdx : 0];

                        return (
                          <div key={`${cat.category}-${cat.label}`} className="border border-zinc-700/50 rounded-lg">
                            <div className="flex items-center gap-2 p-2.5">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => toggleCategory(key)}
                                className="rounded border-zinc-600"
                              />
                              <button
                                onClick={() => toggleCategoryExpand(key)}
                                className="flex items-center gap-1 text-xs text-zinc-400 hover:text-zinc-300"
                              >
                                {isCatExpanded
                                  ? <ChevronDown className="h-3 w-3" />
                                  : <ChevronRight className="h-3 w-3" />}
                              </button>
                              <span className="text-sm text-white font-medium">{cat.label}</span>
                              {cat.category !== 'MONEYLINE' && primaryLineData && (
                                <span className="text-xs text-zinc-400">
                                  Primary: {cat.primaryLine > 0 ? '+' : ''}{cat.primaryLine}
                                </span>
                              )}
                              <span className="text-xs text-zinc-500 ml-auto">
                                {cat.lines.length} line{cat.lines.length !== 1 ? 's' : ''}
                              </span>
                            </div>
                            {isCatExpanded && (
                              <div className="border-t border-zinc-700/50 p-2 max-h-48 overflow-y-auto">
                                <div className="space-y-1">
                                  {cat.lines.map((line, i) => {
                                    const isPrimary = line.line === cat.primaryLine;
                                    return (
                                      <div
                                        key={i}
                                        className={cn(
                                          'flex items-center gap-3 px-2 py-1 rounded text-xs',
                                          isPrimary ? 'bg-amber-500/10 text-amber-300' : 'text-zinc-400'
                                        )}
                                      >
                                        <span className="w-16 font-mono">
                                          {line.line > 0 ? '+' : ''}{line.line}
                                        </span>
                                        <span className="w-20">
                                          O: {line.overOdds > 0 ? '+' : ''}{line.overOdds}
                                        </span>
                                        {line.underOdds !== 0 && (
                                          <span className="w-20">
                                            U: {line.underOdds > 0 ? '+' : ''}{line.underOdds}
                                          </span>
                                        )}
                                        <span className="w-16">{line.costCoins}c</span>
                                        <span className="w-12">{line.bonusMultiplier}x</span>
                                        {isPrimary && (
                                          <span className="text-amber-400 font-medium">PRIMARY</span>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ========== PLAYER PROPS (from Odds API) ========== */}
      {playerProps.length > 0 && (
        <div className="bg-zinc-900 rounded-xl border border-zinc-800 p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-white">
              <Users className="h-5 w-5 inline mr-2 text-blue-400" />
              Player Props
            </h2>
            <div className="flex items-center gap-3">
              <button
                onClick={() => { setPlayerProps([]); }}
                className="text-xs text-zinc-500 hover:text-zinc-300"
              >
                Dismiss
              </button>
              <Button
                size="sm"
                disabled={selectedPlayerCount === 0 || importingOdds}
                onClick={() => handleImportSelected('player')}
              >
                {importingOdds ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
                Import {selectedPlayerCount} Selected
              </Button>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <span className="text-xs text-zinc-500 mr-1">Quick:</span>
            {([
              { cat: 'PLAYER_POINTS', label: 'All Points' },
              { cat: 'PLAYER_REBOUNDS', label: 'All Rebounds' },
              { cat: 'PLAYER_ASSISTS', label: 'All Assists' },
              { cat: 'PLAYER_POINTS_REBOUNDS', label: 'All Pts+Reb' },
            ] as const).map(({ cat, label }) => {
              const active = isPlayerCategoryAllSelected(cat);
              return (
                <button
                  key={cat}
                  onClick={() => togglePlayerCategory(cat)}
                  className={cn(
                    'px-2.5 py-1 rounded text-xs font-medium border transition-colors',
                    active
                      ? 'bg-blue-500/20 border-blue-500/50 text-blue-400'
                      : 'border-zinc-600 text-zinc-400 hover:text-zinc-300 hover:border-zinc-500'
                  )}
                >
                  {active ? <Check className="h-3 w-3 inline mr-1" /> : null}
                  {label}
                </button>
              );
            })}
            <span className="text-zinc-700 mx-0.5">|</span>
            {([
              { count: 5, cats: ['PLAYER_POINTS', 'PLAYER_REBOUNDS', 'PLAYER_ASSISTS'], label: 'Top 5/team (Pts, Reb, Ast)', color: 'purple' },
              { count: 3, cats: undefined, label: 'Top 3/team (All Lines)', color: 'emerald' },
            ] as const).map(({ count, cats, label, color }) => {
              const active = isTopScorersAllSelected(count, cats as string[] | undefined);
              const colors = {
                purple: active ? 'bg-purple-500/20 border-purple-500/50 text-purple-400' : '',
                emerald: active ? 'bg-emerald-500/20 border-emerald-500/50 text-emerald-400' : '',
              };
              return (
                <button
                  key={label}
                  onClick={() => toggleTopScorers(count, cats as string[] | undefined)}
                  className={cn(
                    'px-2.5 py-1 rounded text-xs font-medium border transition-colors',
                    active
                      ? colors[color]
                      : 'border-zinc-600 text-zinc-400 hover:text-zinc-300 hover:border-zinc-500'
                  )}
                >
                  {active ? <Check className="h-3 w-3 inline mr-1" /> : null}
                  {label}
                </button>
              );
            })}
          </div>

          <div className="space-y-3">
            {playerProps.map((gpp) => {
              const gameKey = `player-${gpp.gameId}`;
              const isExpanded = expandedGames.has(gameKey);
              const totalCats = gpp.players.reduce((a, p) => a + p.categories.length, 0);
              // Collect all category keys for this game (for Quick Add All)
              const allGamePlayerKeys = gpp.players.flatMap((p) =>
                p.categories.map((c) => selKey(gpp.gameId, c.category, p.playerName))
              );
              const allGameSelected = allGamePlayerKeys.length > 0 && allGamePlayerKeys.every((k) => selectedCategories.has(k));
              return (
                <div key={gpp.gameId} className="border border-zinc-700 rounded-lg overflow-hidden">
                  <div className="flex items-center bg-zinc-800">
                    <button
                      onClick={() => toggleGameExpand(gameKey)}
                      className="flex-1 flex items-center gap-2 p-3 hover:bg-zinc-750 transition-colors text-left"
                    >
                      {isExpanded ? <ChevronDown className="h-4 w-4 text-zinc-400" /> : <ChevronRight className="h-4 w-4 text-zinc-400" />}
                      <span className="text-sm font-medium text-white">{gpp.awayTeam} @ {gpp.homeTeam}</span>
                      <span className="text-xs text-zinc-500 ml-auto">{gpp.players.length} players · {totalCats} categories</span>
                    </button>
                    <button
                      onClick={() => {
                        setSelectedCategories((prev) => {
                          const next = new Set(prev);
                          if (allGameSelected) {
                            allGamePlayerKeys.forEach((k) => next.delete(k));
                          } else {
                            allGamePlayerKeys.forEach((k) => next.add(k));
                          }
                          return next;
                        });
                      }}
                      className={cn(
                        'px-3 py-1 mr-3 rounded text-xs font-medium border transition-colors',
                        allGameSelected
                          ? 'bg-blue-500/20 border-blue-500/50 text-blue-400'
                          : 'border-zinc-600 text-zinc-400 hover:text-zinc-300 hover:border-zinc-500'
                      )}
                    >
                      {allGameSelected ? 'Deselect All' : 'Quick Add All'}
                    </button>
                  </div>
                  {isExpanded && (
                    <div className="p-3 space-y-2">
                      {gpp.players.map((player) => {
                        const playerKey = `${gpp.gameId}|${player.playerName}`;
                        const isPlayerExpanded = expandedPlayers.has(playerKey);
                        const projectedPts = player.categories.find((c) => c.category === 'PLAYER_POINTS')?.primaryLine;
                        return (
                          <div key={player.playerName} className="border border-zinc-700/50 rounded-lg">
                            <button
                              onClick={() => togglePlayerExpand(playerKey)}
                              className="w-full flex items-center gap-2 p-2.5 hover:bg-zinc-800/50 transition-colors text-left"
                            >
                              {isPlayerExpanded
                                ? <ChevronDown className="h-3.5 w-3.5 text-zinc-400" />
                                : <ChevronRight className="h-3.5 w-3.5 text-zinc-400" />}
                              <span className="text-sm text-white">{lastFirst(player.playerName)}</span>
                              {projectedPts !== undefined && (
                                <span className="text-xs text-amber-400/70">{projectedPts} points</span>
                              )}
                              <span className="text-xs text-zinc-500 ml-auto">{player.categories.length} categories</span>
                            </button>
                            {isPlayerExpanded && (
                              <div className="border-t border-zinc-700/50 p-2 space-y-2">
                                {player.categories.map((cat) => {
                                  const key = selKey(gpp.gameId, cat.category, player.playerName);
                                  const isSelected = selectedCategories.has(key);
                                  const isCatExpanded = expandedCategories.has(key);

                                  return (
                                    <div key={cat.category} className="border border-zinc-700/30 rounded">
                                      <div className="flex items-center gap-2 p-2">
                                        <input
                                          type="checkbox"
                                          checked={isSelected}
                                          onChange={() => toggleCategory(key)}
                                          className="rounded border-zinc-600"
                                        />
                                        <button
                                          onClick={() => toggleCategoryExpand(key)}
                                          className="flex items-center gap-1 text-xs text-zinc-400 hover:text-zinc-300"
                                        >
                                          {isCatExpanded
                                            ? <ChevronDown className="h-3 w-3" />
                                            : <ChevronRight className="h-3 w-3" />}
                                        </button>
                                        <span className="text-sm text-white">{cat.label}</span>
                                        <span className="text-xs text-zinc-400">
                                          O/U {cat.primaryLine}
                                        </span>
                                        <span className="text-xs text-zinc-500 ml-auto">
                                          {cat.lines.length} line{cat.lines.length !== 1 ? 's' : ''}
                                        </span>
                                      </div>
                                      {isCatExpanded && (
                                        <div className="border-t border-zinc-700/30 p-2 max-h-36 overflow-y-auto">
                                          <div className="space-y-1">
                                            {cat.lines.map((line, i) => {
                                              const isPrimary = line.line === cat.primaryLine;
                                              return (
                                                <div
                                                  key={i}
                                                  className={cn(
                                                    'flex items-center gap-3 px-2 py-1 rounded text-xs',
                                                    isPrimary ? 'bg-blue-500/10 text-blue-300' : 'text-zinc-400'
                                                  )}
                                                >
                                                  <span className="w-12 font-mono">{line.line}</span>
                                                  <span className="w-20">
                                                    O: {line.overOdds > 0 ? '+' : ''}{line.overOdds}
                                                  </span>
                                                  <span className="w-20">
                                                    U: {line.underOdds > 0 ? '+' : ''}{line.underOdds}
                                                  </span>
                                                  <span className="w-16">{line.costCoins}c</span>
                                                  <span className="w-12">{line.bonusMultiplier}x</span>
                                                  {isPrimary && (
                                                    <span className="text-blue-400 font-medium">PRIMARY</span>
                                                  )}
                                                </div>
                                              );
                                            })}
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Fetch Odds button (shown when no odds loaded yet) */}
      {teamProps.length === 0 && playerProps.length === 0 && games.length > 0 && (
        <div className="bg-zinc-900 rounded-xl border border-zinc-800 p-6 mb-6 space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-zinc-400">Fetch odds from The Odds API for {games.length} game{games.length !== 1 ? 's' : ''}</p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowGameSelector(!showGameSelector)}
                className="text-xs text-zinc-500 hover:text-zinc-300 transition-colors underline"
              >
                {showGameSelector ? 'Hide' : 'Select games'}
              </button>
              <Button
                size="sm"
                onClick={() => {
                  if (showGameSelector && selectedGameIds.size > 0) {
                    handleFetchOdds([...selectedGameIds]);
                  } else {
                    handleFetchOdds();
                  }
                }}
                disabled={fetchingOdds || (showGameSelector && selectedGameIds.size === 0)}
                className="bg-amber-600 hover:bg-amber-700 text-white"
              >
                {fetchingOdds ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Download className="h-3 w-3 mr-1" />}
                {showGameSelector && selectedGameIds.size > 0
                  ? `Fetch ${selectedGameIds.size} Game${selectedGameIds.size !== 1 ? 's' : ''}`
                  : 'Fetch All'}
              </Button>
            </div>
          </div>

          {/* Game selector */}
          {showGameSelector && (
            <div className="space-y-2 border-t border-zinc-800 pt-3">
              <div className="flex items-center justify-between">
                <p className="text-xs text-zinc-500">Select specific games to fetch odds for:</p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setSelectedGameIds(new Set(games.map((g) => g.id)))}
                    className="text-[10px] text-zinc-500 hover:text-zinc-300 transition-colors"
                  >
                    Select all
                  </button>
                  <button
                    onClick={() => setSelectedGameIds(new Set())}
                    className="text-[10px] text-zinc-500 hover:text-zinc-300 transition-colors"
                  >
                    Clear
                  </button>
                </div>
              </div>
              {games.map((game) => (
                <label
                  key={game.id}
                  className={cn(
                    'flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors',
                    selectedGameIds.has(game.id) ? 'bg-amber-600/10 border border-amber-600/30' : 'bg-zinc-800/50 border border-transparent hover:border-zinc-700',
                  )}
                >
                  <input
                    type="checkbox"
                    checked={selectedGameIds.has(game.id)}
                    onChange={() => {
                      setSelectedGameIds((prev) => {
                        const next = new Set(prev);
                        if (next.has(game.id)) next.delete(game.id);
                        else next.add(game.id);
                        return next;
                      });
                    }}
                    className="rounded border-zinc-600 text-amber-600 focus:ring-amber-600"
                  />
                  <span className="text-sm text-zinc-300">
                    {game.awayTeam} @ {game.homeTeam}
                  </span>
                  <span className="text-xs text-zinc-500 ml-auto">
                    {new Date(game.gameTime).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                  </span>
                </label>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ========== IMPORTED PROPS ========== */}
      <div className="bg-zinc-900 rounded-xl border border-zinc-800 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-white">Imported Props ({props.length})</h2>
          <div className="flex items-center gap-2">
            {undercard.status !== 'COMPLETED' && pendingPropsCount > 0 && allGamesSettled && (
              <Button
                size="sm"
                onClick={handleAutoSettle}
                disabled={autoSettling}
                className="bg-amber-600 hover:bg-amber-700 text-white"
              >
                {autoSettling ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Zap className="h-3 w-3 mr-1" />}
                Settle All ({pendingPropsCount})
              </Button>
            )}
            {props.length > 0 && (
              <button
                onClick={async () => {
                  if (!confirm(`Delete all ${props.length} props? This cannot be undone.`)) return;
                  try {
                    const res = await authFetch(`/api/admin/main-events/${eventId}/undercards/${undercardId}/props`, {
                      method: 'DELETE',
                    });
                    if (res.ok) {
                      const data = await res.json();
                      showSuccess(`Deleted ${data.data?.deleted ?? props.length} props`);
                      fetchData();
                    } else {
                      const data = await res.json();
                      setError(data.error || 'Failed to delete props');
                    }
                  } catch {
                    setError('Failed to delete props');
                  }
                }}
                className="flex items-center gap-1 text-sm text-zinc-500 hover:text-red-400 transition-colors"
              >
                <X className="h-4 w-4" />
                Delete All
              </button>
            )}
            <button
              onClick={() => setShowAddProp(!showAddProp)}
              className="flex items-center gap-1 text-sm text-red-400 hover:text-red-300 transition-colors"
            >
              <Plus className="h-4 w-4" />
              Add Prop
            </button>
            <button
              onClick={() => setManualModal({ open: true, mode: 'create', existing: null })}
              className="flex items-center gap-1 text-sm text-amber-400 hover:text-amber-300 transition-colors"
            >
              <Plus className="h-4 w-4" />
              Add Manual Prediction
            </button>
            <button
              onClick={() => setCsvModalOpen(true)}
              className="flex items-center gap-1 text-sm text-amber-400/70 hover:text-amber-300 transition-colors"
            >
              <Upload className="h-4 w-4" />
              Import CSV
            </button>
          </div>
        </div>

        {/* Manual prediction settlement summary */}
        {(() => {
          const manualProps = props.filter((p) => p.propType && p.propType !== 'ODDS_BASED');
          if (manualProps.length === 0) return null;
          const pending = manualProps.filter((p) => p.result === 'PENDING').length;
          const settled = manualProps.filter((p) => p.result !== 'PENDING').length;
          return (
            <div className="mb-3 px-3 py-2 rounded-lg bg-amber-500/5 border border-amber-500/20 text-xs text-amber-200/80 flex items-center gap-3">
              <span className="font-bold text-amber-300">Manual predictions:</span>
              <span><span className="text-amber-300 font-mono">{pending}</span> pending</span>
              <span className="text-zinc-600">·</span>
              <span><span className="text-emerald-300 font-mono">{settled}</span> settled</span>
              <span className="text-zinc-600">·</span>
              <span><span className="text-zinc-300 font-mono">{manualProps.length}</span> total</span>
            </div>
          );
        })()}

        {/* Add prop form */}
        {showAddProp && (
          <form onSubmit={handleAddProp} className="mb-4 p-3 bg-zinc-800 rounded-lg border border-zinc-700 space-y-3">
            <div>
              <label className="block text-xs text-zinc-400 mb-1">Description *</label>
              <input
                type="text"
                value={propForm.description}
                onChange={(e) => setPropForm((p) => ({ ...p, description: e.target.value }))}
                required
                placeholder="e.g., Duke wins by 10+"
                className="w-full px-3 py-2 rounded-lg bg-zinc-900 border border-zinc-700 text-white text-sm focus:border-red-500 focus:outline-none"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-zinc-400 mb-1">Cost (coins) *</label>
                <input
                  type="number"
                  value={propForm.costCoins}
                  onChange={(e) => setPropForm((p) => ({ ...p, costCoins: Number(e.target.value) }))}
                  min={1}
                  className="w-full px-3 py-2 rounded-lg bg-zinc-900 border border-zinc-700 text-white text-sm focus:border-red-500 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-xs text-zinc-400 mb-1">Bonus Multiplier *</label>
                <input
                  type="number"
                  step="0.1"
                  value={propForm.bonusMultiplier}
                  onChange={(e) => setPropForm((p) => ({ ...p, bonusMultiplier: Number(e.target.value) }))}
                  min={1}
                  className="w-full px-3 py-2 rounded-lg bg-zinc-900 border border-zinc-700 text-white text-sm focus:border-red-500 focus:outline-none"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setShowAddProp(false)} className="px-3 py-1.5 text-sm text-zinc-400">
                Cancel
              </button>
              <Button type="submit" size="sm">
                <Plus className="h-3 w-3 mr-1" /> Add
              </Button>
            </div>
          </form>
        )}

        {/* Settle prop modal */}
        {settlingProp && (
          <div className="mb-4 p-4 bg-zinc-800 rounded-lg border border-amber-500/30 space-y-3">
            <p className="text-sm font-medium text-white">
              Settle: {props.find((p) => p.id === settlingProp)?.description}
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setSettlingPropResult('HIT')}
                className={cn(
                  'flex-1 py-2 rounded-lg text-sm font-bold border transition-colors',
                  settlingPropResult === 'HIT'
                    ? 'bg-green-500/20 border-green-500/50 text-green-400'
                    : 'bg-zinc-900 border-zinc-700 text-zinc-400'
                )}
              >
                HIT
              </button>
              <button
                onClick={() => setSettlingPropResult('MISS')}
                className={cn(
                  'flex-1 py-2 rounded-lg text-sm font-bold border transition-colors',
                  settlingPropResult === 'MISS'
                    ? 'bg-red-500/20 border-red-500/50 text-red-400'
                    : 'bg-zinc-900 border-zinc-700 text-zinc-400'
                )}
              >
                MISS
              </button>
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={() => setSettlingProp(null)} className="px-3 py-1.5 text-sm text-zinc-400">
                Cancel
              </button>
              <Button size="sm" variant="destructive" onClick={handleSettleProp} disabled={settling}>
                {settling ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
                Settle Prop
              </Button>
            </div>
          </div>
        )}

        {/* Props list */}
        {props.length === 0 ? (
          <p className="text-sm text-zinc-500 py-4 text-center">No props yet</p>
        ) : (
          <div className="space-y-2">
            {props.map((prop) => (
              <div
                key={prop.id}
                className={cn(
                  'p-3 rounded-lg border text-sm',
                  prop.result !== 'PENDING' ? 'bg-zinc-800/50 border-zinc-700' : 'bg-zinc-800 border-zinc-700'
                )}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start gap-2">
                      {prop.propType && prop.propType !== 'ODDS_BASED' && (
                        <span className="shrink-0 mt-0.5 px-1.5 py-0.5 rounded text-[9px] font-bold tracking-wider uppercase bg-amber-500/10 text-amber-300 border border-amber-500/30">
                          Manual
                        </span>
                      )}
                      <p className="text-white line-clamp-2 leading-snug">{prop.description}</p>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-zinc-500 mt-1">
                      <span>{prop.costCoins} coins</span>
                      <span>{prop.bonusMultiplier}x bonus</span>
                      {prop.pickMode === 'YES_NO' && <span className="text-amber-400">Yes/No</span>}
                      {prop._count?.selections !== undefined && (
                        <span>{prop._count.selections} purchased</span>
                      )}
                    </div>
                  </div>
                  {prop.result !== 'PENDING' ? (
                    <span
                      className={cn(
                        'px-2 py-0.5 rounded text-xs font-bold border',
                        PROP_RESULT_COLORS[prop.result] || PROP_RESULT_COLORS.PENDING
                      )}
                    >
                      {prop.result}
                    </span>
                  ) : (
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => {
                          setSettlingProp(prop.id);
                          setSettlingPropResult('HIT');
                        }}
                        className="px-2 py-1 rounded text-xs font-medium bg-amber-500/10 text-amber-400 border border-amber-500/30 hover:bg-amber-500/20 transition-colors"
                      >
                        Settle
                      </button>
                      {prop.propType && prop.propType !== 'ODDS_BASED' && (prop._count?.selections ?? 0) === 0 && (
                        <button
                          onClick={() => {
                            // Edit button is only rendered for manual props (gated above), so this cast is safe.
                            if (prop.propType === 'ODDS_BASED') return;
                            setManualModal({
                              open: true,
                              mode: 'edit',
                              existing: {
                                id: prop.id,
                                description: prop.description,
                                costCoins: prop.costCoins,
                                bonusMultiplier: prop.bonusMultiplier,
                                propType: prop.propType,
                                pickMode: prop.pickMode,
                                subjectTeam: prop.subjectTeam ?? null,
                                subjectPlayer: prop.subjectPlayer ?? null,
                                threshold: prop.threshold ?? null,
                                statCategory: prop.statCategory ?? null,
                                gameId: prop.gameId ?? null,
                              },
                            });
                          }}
                          className="px-2 py-1 rounded text-xs font-medium text-zinc-500 hover:text-amber-300 transition-colors"
                          title="Edit prediction"
                        >
                          <Pencil className="h-3 w-3" />
                        </button>
                      )}
                      {(prop._count?.selections ?? 0) === 0 && (
                        <button
                          onClick={async () => {
                            if (!confirm(`Delete "${prop.description}"?`)) return;
                            try {
                              const res = await authFetch(
                                `/api/admin/main-events/${eventId}/undercards/${undercardId}/props?propId=${prop.id}`,
                                { method: 'DELETE' }
                              );
                              if (res.ok) {
                                showSuccess('Prop deleted');
                                fetchData();
                              } else {
                                const data = await res.json();
                                setError(data.error || 'Failed to delete prop');
                              }
                            } catch {
                              setError('Failed to delete prop');
                            }
                          }}
                          className="px-2 py-1 rounded text-xs font-medium text-zinc-500 hover:text-red-400 transition-colors"
                          title="Delete prop"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <ManualPredictionModal
        open={manualModal.open}
        mode={manualModal.mode}
        mainEventId={eventId}
        undercardId={undercardId}
        games={games.map((g) => ({ id: g.id, homeTeam: g.homeTeam, awayTeam: g.awayTeam }))}
        pointsPerCorrectPick={undercard?.pointsPerCorrectPick ?? 2}
        existingProp={manualModal.existing}
        onClose={() => setManualModal({ open: false, mode: 'create', existing: null })}
        onSuccess={(msg) => {
          showSuccess(msg);
          fetchData();
        }}
        onError={(msg) => setError(msg)}
      />

      <ManualPredictionCsvModal
        open={csvModalOpen}
        mainEventId={eventId}
        undercardId={undercardId}
        onClose={() => setCsvModalOpen(false)}
        onSuccess={(msg) => {
          showSuccess(msg);
          fetchData();
        }}
      />
    </div>
  );
}
